import { Component, ViewChild, ElementRef, input, output, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { escapeRegExp } from 'lodash';
import { AutoComplete, AutoCompleteModule } from 'primeng/autocomplete';
import { TranslateModule } from '@ngx-translate/core';
import { KuiLoaderModule } from '../loader/loader.module';
import { KuiIconModule } from '../icon';
import { KuiBadgeModule } from '../badge';
import { CommonModule } from '@angular/common';

export interface KuiAutoCompleteItem {
    id: string;
    name: string;
}

@Component({
    selector: 'kui-tag-input',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        AutoCompleteModule,
        TranslateModule,
        KuiLoaderModule,
        KuiIconModule,
        KuiBadgeModule
    ],
    templateUrl: './tag-input.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KuiTagInputComponent {
    remoteSearch = input<boolean>(false);
    loading = input<boolean>(false);
    invalid = input<boolean>(false);
    multiple = input<boolean>(true);
    freeText = input<boolean>(false);
    placeholder = input<string>();
    disabled = input<boolean>(undefined);
    autoCompleteItems = input<KuiAutoCompleteItem[]>([]);

    @ViewChild(AutoComplete, { static: true }) autoComplete: AutoComplete;

    selectedItems = input.required<KuiAutoCompleteItem[]>();

    searchTermChanged = output<string>();
    itemAdded = output<KuiAutoCompleteItem>();
    itemRemoved = output<KuiAutoCompleteItem>();

    private _suggestedItems: KuiAutoCompleteItem[] = [];
    private _searchTerm = '';

    constructor(private el: ElementRef) { }

    /**
     * Suggestion items could be provided as external items
     * Here it is wrapper so that when `remoteSearch` is enabled, the list can be populated externally.
     */
    get suggestionItems() {
        return this.remoteSearch() ? this.autoCompleteItems() : this._suggestedItems;
    }

    doSearch({ query }: { query: string }) {

        this._searchTerm = query;

        if (this.remoteSearch()) {
            return this.searchTermChanged.emit(query);
        }

        if (this.autoCompleteItems() && this.autoCompleteItems().length > 0) {
            // Allow only text searches
            const searchExpression = new RegExp(escapeRegExp(query), 'i');
            this._suggestedItems = this.autoCompleteItems()
                .filter(item => !this.selectedItems().find(x => x?.id === item.id))
                .filter(item => searchExpression.test(item.name))
                .slice(0, 4);
        }
    }

    highlightTerm(value: string) {
        if (!this._searchTerm || !value) {
            return value;
        }
        return value.replace(
            new RegExp(escapeRegExp(this._searchTerm), 'ig'),
            (term) => `<span class="text-primary">${term}</span>`
        );
    }

    addItem(item: any) {
        // this changes depending on if you select something from the dropdown or the add button
        let tag = item;
        if (item?.originalEvent) {
            tag = item.value;
        }

        // When the user fully types out a value and clicks on the suggestion in the dropdown this "addItem" method gets called twice. 
        // Once due to a "change" event, and once due to a click "event".
        // Do some nasty checking to ensure that we don't emit the same item twice, unless it's a single select then we should emit...
        if (this.multiple()) {
            if (!this.selectedItems().includes(tag)) {
                this.itemAdded.emit(tag)
            }
        } else {
            this.itemAdded.emit(tag);
        }
    }

    removeItem(item: KuiAutoCompleteItem) {
        if (item) {
            this.itemRemoved.emit(item);
        }
    }

    clearItem() {
        // Only intended for single selects, hooks onto when input box is cleared
        if (!this.multiple() && this.selectedItems().length === 1) {
            this.removeItem(this.selectedItems()[0]);
        }
    }

    onKeyUp(event: { key: string}) {
        if (event.key === 'Enter' && this.freeText()) {
            // This is free text, user can enter anything and we will turn it into a tag.
            const input = this.el.nativeElement.querySelector('.kui-tag-input__textbox') as HTMLInputElement;
            if (input) {
                const value = input.value;
                if (value) {
                    input.value = '';
                    this.addItem({ id: value, name: value });
                }
            }
        }
    }
}